import { useRef, useState, useEffect } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { getDrafts, syncDrafts } from "@/lib/offlineDrafts";
import { FYSelector } from "@/components/FYSelector";
import { DateFilterPopup } from "@/components/DateFilterPopup";
import { useGlobalShortcuts } from "@/lib/useGlobalShortcuts";
import { useSoftwareConfig } from "@/lib/softwareConfig";
import { LinkSimple } from "@phosphor-icons/react";
import {
  House, Users, Package, Receipt, ChartBar, GearSix, ChartLine,
  ArrowsClockwise, FileText, UsersThree, SignOut, Camera, UserCircle, List, X, Truck, ShoppingCart, Bank, Cube, DeviceMobile, Sun, Moon, Trash, FilePdf, Calculator, ShieldStar, Wallet, Notebook,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout() {
  const { user, logout, checkAuth } = useAuth();
  useGlobalShortcuts();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isStaffOrAdmin = user?.role === "admin" || user?.role === "staff" || user?.role === "super_admin";
  const tiEnabled = !!user?.transport_invoice_enabled;
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [draftMsg, setDraftMsg] = useState("");

  useEffect(() => {
    const trySync = async () => {
      if (!navigator.onLine || !getDrafts().length) return;
      const r = await syncDrafts();
      if (r.synced > 0) {
        setDraftMsg(`✓ ${r.synced} offline draft invoice${r.synced > 1 ? "s" : ""} auto-saved!`);
        setTimeout(() => setDraftMsg(""), 8000);
      }
    };
    const goOn = () => { setOffline(false); trySync(); };
    const goOff = () => setOffline(true);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    trySync();
    return () => { window.removeEventListener("online", goOn); window.removeEventListener("offline", goOff); };
  }, []);

  const navItems = [
    { path: "/dashboard", icon: House, label: "Dashboard", show: true },
    { path: "/customers", icon: Users, label: "Customers", show: true },
    { path: "/products", icon: Package, label: "Products", show: true },
    { path: "/ledger", icon: Cube, label: "Ledger", show: isStaffOrAdmin },
    { path: "/invoices", icon: Receipt, label: "Sales", show: true },
    // Only rendered when the super_admin has enabled the Transport Invoice
    // module for this user. `tiEnabled` is derived from the JWT payload so
    // the entry pops in/out on next login without any hard-refresh.
    { path: "/transport-invoices", icon: Truck, label: "Transport Invoices", show: tiEnabled && isStaffOrAdmin },
    { path: "/purchases", icon: ShoppingCart, label: "Purchases", show: isStaffOrAdmin },
    { path: "/godown", icon: Package, label: "Godown", show: isStaffOrAdmin },
    { path: "/manufacturing", icon: Cube, label: "Manufacturing", show: isStaffOrAdmin },
    { path: "/pdf-tools", icon: FilePdf, label: "PDF Tools", show: isStaffOrAdmin },
    { path: "/recurring", icon: ArrowsClockwise, label: "Recurring", show: isStaffOrAdmin },
    { path: "/challans", icon: Truck, label: "Challans", show: isStaffOrAdmin },
    { path: "/vouchers", icon: Receipt, label: "Vouchers", show: isStaffOrAdmin },
    { path: "/ledger-views", icon: ChartLine, label: "Ledger Views", show: isStaffOrAdmin },
    { path: "/bank", icon: Bank, label: "Bank & Reconciliation", show: isStaffOrAdmin },
    { path: "/notes", icon: Receipt, label: "Debit/Credit Notes", show: isStaffOrAdmin },
    { path: "/letterheads", icon: FileText, label: "Letterheads", show: isStaffOrAdmin },
    { path: "/reports", icon: ChartLine, label: "Reports", show: true },
    { path: "/reports/gstr1", icon: FileText, label: "GSTR-1", show: true },
    { path: "/schedule-iii", icon: Notebook, label: "Schedule III", show: true },
    { path: "/users", icon: UsersThree, label: "Users", show: isAdmin },
    { path: "/wallet", icon: Wallet, label: "Wallet", show: isAdmin },
    { path: "/settings", icon: GearSix, label: "Settings", show: isAdmin },
    { path: "/recycle-bin", icon: Trash, label: "Recycle Bin", show: isAdmin },
    { path: "/super-admin", icon: ShieldStar, label: "Super Admin", show: user?.role === "super_admin" },
    { path: "/install", icon: DeviceMobile, label: "Install App (Desktop/Mobile)", show: true },
  ].filter(item => item.show);

  // ---- Software Creation overrides (Super Admin controlled, global) ----
  const swCfg = useSoftwareConfig();
  let renderItems = navItems;
  if (swCfg?.tabs) {
    const byRoute = {};
    swCfg.tabs.forEach((t) => { if (!t.custom && !t.tool) byRoute[t.route] = t; });
    renderItems = navItems
      .map((i, idx) => {
        const c = byRoute[i.path];
        return { ...i, label: c?.label || i.label, hidden: c ? c.visible === false : false, order: c?.order ?? idx };
      })
      .filter((i) => !i.hidden)
      .sort((a, b) => a.order - b.order);
    swCfg.tabs.filter((t) => t.custom && t.visible !== false && t.route).forEach((t) => {
      renderItems.push({ path: t.route, label: t.label, icon: LinkSimple, external: !!t.external || /^https?:/i.test(t.route), custom: true });
    });
  }

  const roleLabel = { super_admin: "Super Admin", admin: "Admin", staff: "Staff", viewer: "Viewer" };
  const roleColor = { super_admin: "bg-purple-100 text-purple-700", admin: "bg-red-100 text-red-700", staff: "bg-blue-100 text-blue-700", viewer: "bg-slate-100 text-slate-600" };

  const SidebarContent = () => (
    <>
      <div className="p-4 sm:p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-900 rounded-sm flex items-center justify-center flex-shrink-0">
            <ChartBar size={22} weight="duotone" className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-black tracking-tight text-slate-900 truncate">LokTech Dex Pro</h1>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider truncate">{user?.company_name || "Hub"}</p>
          </div>
        </div>
      </div>

      <FYSelector user={user} />

      <nav className="flex-1 p-3 sm:p-4 space-y-0.5 sm:space-y-1 overflow-y-auto">
        {renderItems.map((item) => (
          item.external ? (
            <a key={item.path} href={item.path} target="_blank" rel="noreferrer"
              data-testid={`nav-${(item.label || "").toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              className="flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-sm transition-all duration-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              <item.icon size={18} weight="regular" />
              <span className="font-semibold text-sm">{item.label}</span>
            </a>
          ) : (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-sm transition-all duration-200 ${
                isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon size={18} weight={isActive ? "duotone" : "regular"} />
                <span className="font-semibold text-sm">{item.label}</span>
              </>
            )}
          </NavLink>
          )
        ))}
      </nav>

      <div className="p-3 sm:p-4 border-t border-slate-200">
        <div className="px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center gap-3 mb-3">
            <AvatarUpload user={user} onUpdated={checkAuth} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || user?.email}</p>
                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${roleColor[user?.role] || roleColor.viewer}`}>
                  {roleLabel[user?.role] || "Viewer"}
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200 border border-slate-200"
          >
            <SignOut size={16} /> Sign Out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
            <ChartBar size={18} weight="duotone" className="text-white" />
          </div>
          <span className="text-sm font-black tracking-tight text-slate-900">LokTech Dex Pro</span>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle variant="mobile" />
          <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn" className="p-2 text-slate-600 hover:bg-slate-100 rounded-sm">
            {sidebarOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Floating top-right controls — desktop only. Theme toggle stays
          here. The "Download App" badge was removed per user request; the
          Mobile App entry in the sidebar (below Settings) remains. */}
      <div className="hidden lg:flex fixed top-4 right-4 z-30 items-center gap-2">
        <ThemeToggle variant="desktop" />
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar - Desktop: always visible, Mobile: slide-in */}
      <aside className={`fixed lg:static z-50 top-0 left-0 h-full w-64 bg-white border-r border-slate-200 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
        {offline && (
          <div data-testid="offline-banner" className="bg-amber-400 text-slate-900 text-center text-xs font-bold py-2 px-4 sticky top-0 z-30">
            ⚠ Offline mode — no internet connection. You are viewing last synced data{getDrafts().length > 0 ? ` · ${getDrafts().length} draft invoice(s) pending sync` : ""}.
          </div>
        )}
        {draftMsg && (
          <div data-testid="draft-sync-banner" className="bg-emerald-500 text-white text-center text-xs font-bold py-2 px-4 sticky top-0 z-30">
            {draftMsg}
          </div>
        )}
        <Outlet />
        <DateFilterPopup />
      </main>
    </div>
  );
}

function AvatarUpload({ user, onUpdated }) {
  const fileRef = useRef(null);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/auth/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="relative group cursor-pointer flex-shrink-0" onClick={() => fileRef.current?.click()} data-testid="avatar-upload-trigger">
      <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/webp" onChange={handleUpload} className="hidden" />
      {user?.avatar ? (
        <img src={user.avatar} alt="Avatar" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-slate-200" />
      ) : (
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-200 flex items-center justify-center">
          <UserCircle size={26} weight="duotone" className="text-slate-500" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
        <Camera size={14} className="text-white" />
      </div>
    </div>
  );
}

/**
 * Dark / Light theme toggle — persists choice to localStorage under
 * `gst.theme`. The initial value is also read in `public/index.html`
 * so the first paint already matches the user's preference.
 */
function ThemeToggle({ variant }) {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });
  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add("dark");
      localStorage.setItem("gst.theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("gst.theme", "light");
    }
  }, [dark]);
  const Icon = dark ? Sun : Moon;
  const label = dark ? "Switch to light mode" : "Switch to dark mode";
  if (variant === "mobile") {
    return (
      <button
        onClick={() => setDark(d => !d)}
        data-testid="theme-toggle-btn"
        title={label}
        aria-label={label}
        className="p-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
      >
        <Icon size={20} weight="duotone" />
      </button>
    );
  }
  return (
    <button
      onClick={() => setDark(d => !d)}
      data-testid="theme-toggle-btn"
      title={label}
      aria-label={label}
      className="no-dark flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md hover:bg-slate-50 transition-all"
    >
      <Icon size={18} weight="duotone" className="text-slate-700" />
    </button>
  );
}

